import React, { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  MessageOutlined,
  CloseOutlined,
  SendOutlined,
  RobotOutlined,
  FilePdfOutlined,
  DownloadOutlined,
} from "@ant-design/icons";
import { post } from "../services/services";
import { baseURL } from "../services/apiConstants";
import "./ChatBot.css";

export default function ChatBot({ apiUrl }) {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      id: 1,
      type: "bot",
      text: "Hello! 👋 Welcome to MarineByte. How can I help you today?",
    },

  ]);
  const [inputValue, setInputValue] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  // Download PDF using anchor-click pattern
  const handlePdfDownload = (url) => {
    const link = document.createElement("a");
    link.href = url;
    link.download = url.split("/").pop() || "document.pdf";
    link.target = "_blank";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Render message content — detect PDF URLs and show download buttons
  const renderMessageContent = (text) => {
    // Regex to match URLs ending with .pdf
    const urlRegex = /(https?:\/\/[^\s]+\.pdf(?:\?[^\s]*)?)/gi;
    const parts = text.split(urlRegex);

    if (parts.length === 1) {
      // No PDF URL found, return plain text
      return text;
    }

    return parts.map((part, index) => {
      if (urlRegex.test(part)) {
        // Reset regex lastIndex since we use 'g' flag
        urlRegex.lastIndex = 0;
        const fileName = part.split("/").pop().split("?")[0] || "document.pdf";
        return (
          <button
            key={index}
            className="chatbot-pdf-link"
            onClick={() => handlePdfDownload(part)}
            title={fileName}
          >
            <FilePdfOutlined />
            <span className="chatbot-pdf-name">{fileName}</span>
            <DownloadOutlined className="chatbot-pdf-download-icon" />
          </button>
        );
      }
      // Reset regex lastIndex
      urlRegex.lastIndex = 0;
      return part ? <span key={index}>{part}</span> : null;
    });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Fetch bot response from API
  const fetchBotResponse = async (userMessage) => {
    try {
      const formData = new FormData();
      formData.append("message", userMessage);
      if (sessionId) {
        formData.append("session_id", sessionId);
      }

      const data = await post(apiUrl, formData);

      // If error returned from axios
      if (data instanceof Error) {
        throw data;
      }

      // Store session_id from response for conversation continuity
      if (data.session_id) {
        setSessionId(data.session_id);
      }

      const botText =
        data.reply ||
        data.response ||
        data.message ||
        "I received your message. How can I help you further?";

      // Build full download URL if download_url is present
      let downloadUrl = null;
      if (data.download_url) {
        const base = baseURL.endsWith("/") ? baseURL.slice(0, -1) : baseURL;
        downloadUrl = data.download_url.startsWith("http")
          ? data.download_url
          : `${base}${data.download_url}`;
      }

      return { text: botText, downloadUrl };
    } catch (error) {
      console.error("Chatbot API error:", error);
      return { text: "Sorry, I'm having trouble connecting right now. Please try again later. 🙏", downloadUrl: null };
    }
  };

  const handleSend = async () => {
    if (!inputValue.trim()) return;

    const currentInput = inputValue;
    const userMessage = {
      id: Date.now(),
      type: "user",
      text: currentInput,
    };

    setMessages((prev) => [...prev, userMessage]);
    setInputValue("");
    setIsTyping(true);

    // Fetch response from API
    const result = await fetchBotResponse(currentInput);
    const botResponse = {
      id: Date.now() + 1,
      type: "bot",
      text: result.text,
      downloadUrl: result.downloadUrl,
    };
    setMessages((prev) => [...prev, botResponse]);
    setIsTyping(false);
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <>
      {/* Floating Chat Toggle Button */}
      <motion.button
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.95 }}
        onClick={() => setIsOpen(!isOpen)}
        className="chatbot-toggle-btn"
      >
        {isOpen ? <CloseOutlined /> : <MessageOutlined />}
      </motion.button>

      {/* Chat Window */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className="chatbot-window"
          >
            {/* Header */}
            <div className="chatbot-header">
              <div className="chatbot-header-icon">
                <RobotOutlined />
              </div>
              <div className="chatbot-header-info">
                <h3>MarineByte Assistant</h3>
                <p>Online | Usually replies instantly</p>
              </div>
            </div>

            {/* Messages */}
            <div className="chatbot-messages">
              {messages.map((msg) => (
                <motion.div
                  key={msg.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`chatbot-msg-row ${msg.type}`}
                >
                  <div className={`chatbot-msg-bubble ${msg.type}`}>
                    {msg.type === "bot"
                      ? renderMessageContent(msg.text)
                      : msg.text}
                    {msg.type === "bot" && msg.downloadUrl && (
                      <button
                        className="chatbot-pdf-link"
                        onClick={() => handlePdfDownload(msg.downloadUrl)}
                        style={{ marginTop: 8 }}
                      >
                        <FilePdfOutlined />
                        <span className="chatbot-pdf-name">
                          {msg.downloadUrl.split("/").pop() || "report.pdf"}
                        </span>
                        <DownloadOutlined className="chatbot-pdf-download-icon" />
                      </button>
                    )}
                  </div>
                </motion.div>
              ))}

              {/* Typing Indicator */}
              {isTyping && (
                <div className="chatbot-typing">
                  <div className="chatbot-typing-bubble">
                    <span className="chatbot-typing-dot"></span>
                    <span className="chatbot-typing-dot"></span>
                    <span className="chatbot-typing-dot"></span>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <div className="chatbot-input-area">
              <input
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Type your message..."
              />
              <button
                onClick={handleSend}
                disabled={!inputValue.trim()}
                className="chatbot-send-btn"
              >
                <SendOutlined />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
